import { createPdf } from 'pdfmake/build/pdfmake'
import {
  Content,
  PageBreak,
  Style,
  Table,
  TFontDictionary,
  TFontFamilyTypes,
} from 'pdfmake/interfaces'
import { TrusteroLogoFullPath } from '../../components/Icons/Brand'
import * as v from './fonts/arial'
import toPDF from './MarkdownParser'

async function logo() {
  const response = await fetch(TrusteroLogoFullPath)
  const blob = await response.blob()
  const text = await blob.text()
  return text.trim()
}

export class PDFGenerator {
  private fonts: TFontDictionary
  private content: Content[]
  private styles: Record<string, Style>
  private footer?: (page: number, total: number) => Content

  constructor() {
    this.content = []
    this.fonts = {
      Arial: {
        normal: 'ArialMT.ttf',
        bold: 'ArialBoldMT.ttf',
        italics: 'ArialItalicMT.ttf',
        bolditalics: 'ArialBoldItalicMT.ttf',
      },
    }
    this.styles = {
      title: {
        alignment: 'center',
        margin: [20, 190, 20, 80],
      },
      disclaimer: {
        alignment: 'left',
        margin: [20, 650, 20, 30],
      },
      toc: {
        alignment: 'right',
        margin: [150, 30, 20, 30],
      },
      heading: {
        margin: [0, 7, 0, 7],
      },
      footer: {
        margin: [50, 0, 50, 0],
      },
      subheading: {
        margin: [0, 3, 0, 3],
      },
      subsubheading: {
        margin: [0, 1, 0, 1],
      },
      tableHeader: {
        color: 'white',
        fillColor: 'blue',
      },
      tableSubHeader: {
        bold: true,
        fillColor: 'grey',
      },
      ulist: {
        margin: [20, 5, 0, 5],
      },
      h1: {
        bold: true,
        fontSize: 27,
        color: '#0000000',
        characterSpacing: 0.375,
      },
      h2: {
        bold: true,
        fontSize: 16,
        color: '#0000000',
        characterSpacing: 0.3,
      },
      h3: {
        bold: true,
        fontSize: 12,
        color: '#0000000',
        characterSpacing: 0.2175,
      },
      h4: {
        bold: true,
        fontSize: 9,
        color: '#0000000',
        characterSpacing: 0.165,
      },
      body: {
        bold: true,
        fontSize: 8,
        color: '#0000000',
        characterSpacing: 0.165,
      },
      caption: {
        bold: true,
        fontSize: 6.75,
        color: '#0000000',
        characterSpacing: 0.11,
      },
      highlight: {
        background: '#fffff33',
      },
    }
  }

  public addBlock(block: Content): void {
    this.content.push(block)
  }

  public addList(list: string[]): void {
    this.content.push({
      ul: list,
      style: 'ulist',
    })
  }

  public setFooter(fn: (page: number, total: number) => Content): void {
    this.footer = fn
  }

  public addStyle(name: string, style: Style): void {
    this.styles[name] = style
  }

  public addFont(name: string, font: TFontFamilyTypes): void {
    this.fonts[name] = font
  }

  public async addTitlePage(
    title: string,
    subtitle: string,
    prepared: string,
  ): Promise<void> {
    const svg = await logo()
    this.content.push({
      stack: [
        { text: title, style: ['h1'], lineHeight: 2 },
        {
          text: subtitle,
          style: ['h2', 'subtitle'],
        },
        {
          text: [
            {
              text: prepared,
              style: ['h3', 'subtitle'],
            },
            '\n',
            {
              text: new Date().toLocaleDateString(undefined, {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              }),
              style: ['subtitle', 'h3'],
            },
          ],
          margin: [0, 250, 0, 50],
        },
        {
          svg: svg,
        },
      ],
      style: 'title',
    })
  }

  public addTOC(): void {
    this.content.push({
      toc: {
        title: { text: 'TABLE OF CONTENTS', style: 'toc' },
      },
      pageBreak: 'before',
    })
  }

  public addSection(section: number | string, title: string): void {
    this.content.push({
      stack: [
        { text: '', pageBreak: 'before' },
        {
          text: [{ text: `Section ${section}\t` }, { text: title }],
          tocItem: true,
          alignment: 'center',
          style: 'h2',
        } as Content,
      ],
    })
  }

  public addHeading(
    content: string,
    toc?: boolean,
    pageBreak?: PageBreak,
  ): void {
    this.content.push({
      text: content,
      style: 'h2',
      tocItem: toc,
      pageBreak: pageBreak,
    })
  }

  public addSubHeading(content: string): void {
    this.content.push({
      text: content,
      style: 'h3',
    })
  }

  addSubSubHeading(content: string): void {
    this.content.push({
      text: content,
      style: 'h4',
    })
  }

  public addMarkdown(markdown: string): void {
    const [content, _] = toPDF(markdown)
    this.content.push({
      stack: content,
    })
  }

  public toBlob(): Promise<Blob> {
    const doc = createPdf(
      {
        content: this.content,
        styles: this.styles,
        footer: this.footer,
        pageSize: 'A4',
        defaultStyle: {
          font: 'Arial',
        },
      },
      {},
      this.fonts,
      v,
    )

    const promise = new Promise<Blob>((resolve) => {
      doc.getBlob((result: Blob) => {
        resolve(result)
      })
    })

    return promise
  }

  public addTable(table: Table): void {
    this.content.push({
      style: 'table',
      table: table,
      margin: [0, 15, 0, 0],
      pageBreak: 'after',
    })
  }

  public addText(text: string, tabs?: boolean): void {
    this.content.push({
      text: tabs ? `\t\t\t${text}` : text,
      preserveLeadingSpaces: tabs,
    })
  }
}
